import * as THREE from 'three';
import { Track, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { glowSprite, hullTexture } from '../proctex.js';
import { sampleRibbon, clearOfRibbon } from './placement.js';

/**
 * ANCHORAGE-9 — hard-vacuum orbital dock. Capital ships slide past on invisible
 * rails with lit windows and engine glow, floodlit gantries crowd the approach
 * lanes, cargo containers tumble free, and a ringed planet hangs over it all.
 */
export function buildAnchorage9(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('anchorage-9');

  const ribbon = sampleRibbon(track, 80);
  const { cx, cy, cz, maxR } = ribbon;

  // ── sky: near-black space with the faintest galactic band ──
  {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {},
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          vec3 col = mix(vec3(0.012, 0.020, 0.036), vec3(0.002, 0.003, 0.008), pow(h, 0.8));
          // dim milky band slanting across the sky
          float band = exp(-abs(vDir.y * 0.8 + vDir.x * 0.4) * 3.5);
          col += vec3(0.020, 0.026, 0.040) * band;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
  }

  // ── starfield ──
  {
    const count = quality === 'performance' ? 1100 : 2600;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const a = rng.next() * Math.PI * 2;
      const z = rng.range(-1, 1);
      const r = Math.sqrt(1 - z * z);
      pos[i * 3] = Math.cos(a) * r * 3500;
      pos[i * 3 + 1] = z * 3500;
      pos[i * 3 + 2] = Math.sin(a) * r * 3500;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2.2,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const stars = new THREE.Points(geo, mat);
    scene.add(stars);
    objects.push(stars);
    disposables.push(geo, mat);
  }

  // ── planet backdrop: banded ice giant with a bright limb ──
  {
    const geo = new THREE.SphereGeometry(620, 32, 24);
    const mat = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: `
        varying vec3 vNorm; varying vec3 vView;
        void main(){
          vNorm = normalize(normalMatrix * normal);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          vView = normalize(-mv.xyz);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: `
        varying vec3 vNorm; varying vec3 vView;
        void main(){
          float fres = 1.0 - max(0.0, dot(vNorm, vView));
          // latitude bands
          float bands = 0.5 + 0.5 * sin(vNorm.y * 14.0 + sin(vNorm.y * 5.0) * 2.0);
          vec3 deep = vec3(0.05, 0.10, 0.19);
          vec3 pale = vec3(0.14, 0.24, 0.36);
          vec3 col = mix(deep, pale, bands);
          // day side from upper right + atmosphere limb
          float lit = 0.25 + 0.75 * max(0.0, dot(vNorm, normalize(vec3(0.7, 0.5, 0.4))));
          col = col * lit + vec3(0.30, 0.55, 0.85) * pow(fres, 3.0) * 0.8;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const planet = new THREE.Mesh(geo, mat);
    const ang = rng.range(0, Math.PI * 2);
    planet.position.set(cx + Math.cos(ang) * 2500, cy + 700, cz + Math.sin(ang) * 2500);
    scene.add(planet);
    objects.push(planet);
    disposables.push(geo, mat);
  }

  // ── lights: harsh unfiltered sun + cold dock bounce ──
  {
    const hemi = new THREE.HemisphereLight(0x2c4258, 0x05080d, 0.55);
    const sun = new THREE.DirectionalLight(0xf2f6ff, 1.6);
    sun.position.set(cx + 900, cy + 500, cz + 200);
    sun.target.position.set(cx, cy, cz);
    const fill = new THREE.DirectionalLight(0x52e89a, 0.14);
    fill.position.set(cx - 400, cy - 300, cz - 600);
    scene.add(hemi, sun, sun.target, fill);
    objects.push(hemi, sun, sun.target, fill);
  }

  const glowTex = glowSprite();
  disposables.push(glowTex);

  // ── capital ships on rails: instanced hulls + billboarded engine glows ──
  const shipCount = quality === 'performance' ? 4 : 7;
  interface Rail {
    base: THREE.Vector3;
    dir: THREE.Vector3;
    side: THREE.Vector3;
    quat: THREE.Quaternion;
    scale: THREE.Vector3;
    span: number;
    speed: number;
    phase: number;
  }
  const rails: Rail[] = [];
  const hulls = (() => {
    const tex = hullTexture(19);
    disposables.push(tex);
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.7,
      color: 0x39414c,
      roughness: 0.6,
      metalness: 0.7,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, shipCount);
    mesh.frustumCulled = false; // hulls travel far from their initial bounds
    disposables.push(geo, mat);
    const up = new THREE.Vector3(0, 1, 0);
    const lookM = new THREE.Matrix4();
    const target = new THREE.Vector3();
    let made = 0;
    let guard = 0;
    while (made < shipCount && guard++ < shipCount * 40) {
      const ang = rng.range(0, Math.PI * 2);
      const dist = maxR + rng.range(380, 1050);
      const base = new THREE.Vector3(
        cx + Math.cos(ang) * dist,
        cy + rng.range(-260, 280),
        cz + Math.sin(ang) * dist
      );
      // rails run roughly tangential so ships glide across the view
      const dir = new THREE.Vector3(-Math.sin(ang), rng.range(-0.08, 0.08), Math.cos(ang))
        .normalize()
        .multiplyScalar(rng.chance(0.5) ? 1 : -1);
      const span = rng.range(550, 900);
      // reject rails whose sweep crosses near the ribbon
      let ok = true;
      for (let k = -2; k <= 2 && ok; k++) {
        const px = base.x + dir.x * span * (k / 2);
        const pz = base.z + dir.z * span * (k / 2);
        ok = clearOfRibbon(ribbon, px, pz, 240);
      }
      if (!ok) continue;
      target.copy(base).add(dir);
      lookM.lookAt(target, base, up); // orient hull +z along travel
      const quat = new THREE.Quaternion().setFromRotationMatrix(lookM);
      const len = rng.range(230, 420);
      rails.push({
        base,
        dir,
        side: new THREE.Vector3().crossVectors(dir, up).normalize(),
        quat,
        scale: new THREE.Vector3(rng.range(34, 62), rng.range(26, 46), len),
        span,
        speed: rng.range(6, 15),
        phase: rng.range(0, span * 2),
      });
      made++;
    }
    mesh.count = made;
    scene.add(mesh);
    objects.push(mesh);
    return mesh;
  })();
  const engineGlows = (() => {
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      map: glowTex,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0x6ab8ff,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, rails.length * 2);
    mesh.frustumCulled = false;
    disposables.push(geo, mat);
    scene.add(mesh);
    objects.push(mesh);
    return mesh;
  })();

  // ── dock gantries + floodlight beams ──
  {
    const count = quality === 'performance' ? 26 : 54;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x1c242e,
      emissive: 0x52e89a,
      emissiveIntensity: 0.12,
      roughness: 0.7,
      metalness: 0.8,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const beamGeo = new THREE.ConeGeometry(1, 1, 12, 1, true);
    const beamMat = new THREE.MeshBasicMaterial({
      map: glowTex,
      color: 0xbfe3ff,
      transparent: true,
      opacity: 0.06,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const beamCount = quality === 'performance' ? 8 : 16;
    const beams = new THREE.InstancedMesh(beamGeo, beamMat, beamCount);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let beamPlaced = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const ang = rng.range(0, Math.PI * 2);
      const dist = maxR + rng.range(160, 700);
      const x = cx + Math.cos(ang) * dist;
      const z = cz + Math.sin(ang) * dist;
      if (!clearOfRibbon(ribbon, x, z, 130)) continue;
      const y = cy + rng.range(-220, 240);
      const long = rng.chance(0.5);
      dummy.position.set(x, y, z);
      dummy.scale.set(
        long ? rng.range(180, 380) : rng.range(10, 26),
        long ? rng.range(8, 16) : rng.range(120, 320),
        rng.range(10, 26)
      );
      dummy.rotation.set(0, rng.range(0, Math.PI), long && rng.chance(0.3) ? Math.PI / 2 : 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      // hang a floodlight cone off some gantries: apex at the lamp, spreading down
      if (beamPlaced < beamCount && rng.chance(0.4)) {
        const len = rng.range(160, 320);
        dummy.position.set(x, y - len / 2, z);
        dummy.scale.set(rng.range(30, 70), len, rng.range(30, 70));
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        beams.setMatrixAt(beamPlaced, dummy.matrix);
        beamPlaced++;
      }
      placed++;
    }
    mesh.count = placed;
    beams.count = beamPlaced;
    scene.add(mesh, beams);
    objects.push(mesh, beams);
    disposables.push(geo, mat, beamGeo, beamMat);
  }

  // ── drifting cargo containers (staggered tumble) ──
  const containers = (() => {
    const count = quality === 'performance' ? 40 : 90;
    const geo = new THREE.BoxGeometry(3.2, 3.2, 7.6);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x384250,
      emissive: 0x52e89a,
      emissiveIntensity: 0.08,
      roughness: 0.8,
      metalness: 0.5,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    const bases: THREE.Vector3[] = [];
    const quats: THREE.Quaternion[] = [];
    const spins: THREE.Quaternion[] = [];
    const bobs: number[] = [];
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const x = cx + rng.range(-1300, 1300);
      const z = cz + rng.range(-1300, 1300);
      if (!clearOfRibbon(ribbon, x, z, 75)) continue;
      bases.push(new THREE.Vector3(x, cy + rng.range(-180, 200), z));
      const q = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(rng.range(0, 3), rng.range(0, 3), rng.range(0, 3))
      );
      quats.push(q);
      spins.push(
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1)).normalize(),
          rng.range(0.002, 0.01)
        )
      );
      bobs.push(rng.range(0, Math.PI * 2));
      dummy.position.copy(bases[placed]);
      dummy.quaternion.copy(q);
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
    return { mesh, bases, quats, spins, bobs };
  })();

  const m = new THREE.Matrix4();
  const shipPos = new THREE.Vector3();
  const sternPos = new THREE.Vector3();
  const glowDummy = new THREE.Object3D();
  const contDummy = new THREE.Object3D();
  let frameCounter = 0;
  let t = 0;

  return {
    update(dt, camPos, _time) {
      t += dt;
      // capital ships glide along their rails (few instances → update all)
      for (let i = 0; i < rails.length; i++) {
        const r = rails[i];
        const travel = ((t * r.speed + r.phase) % (r.span * 2)) - r.span;
        shipPos.copy(r.base).addScaledVector(r.dir, travel);
        m.compose(shipPos, r.quat, r.scale);
        hulls.setMatrixAt(i, m);
        // twin engine glows at the stern, billboarded to the camera
        sternPos.copy(shipPos).addScaledVector(r.dir, -(r.scale.z / 2 + 5));
        const flick = 0.85 + 0.15 * Math.sin(t * 7 + i * 2.4);
        for (let e = 0; e < 2; e++) {
          glowDummy.position.copy(sternPos).addScaledVector(r.side, (e === 0 ? -1 : 1) * r.scale.x * 0.24);
          glowDummy.lookAt(camPos);
          const s = r.scale.y * 0.9 * flick;
          glowDummy.scale.set(s, s, s);
          glowDummy.updateMatrix();
          engineGlows.setMatrixAt(i * 2 + e, glowDummy.matrix);
        }
      }
      hulls.instanceMatrix.needsUpdate = true;
      engineGlows.instanceMatrix.needsUpdate = true;
      // containers tumble/bob — one third per frame
      if (dt > 0) {
        frameCounter++;
        const count = containers.bases.length;
        const third = Math.ceil(count / 3);
        const start = (frameCounter % 3) * third;
        const end = Math.min(count, start + third);
        for (let i = start; i < end; i++) {
          containers.quats[i].multiply(containers.spins[i]);
          contDummy.position.copy(containers.bases[i]);
          contDummy.position.y += Math.sin(t * 0.25 + containers.bobs[i]) * 4;
          contDummy.quaternion.copy(containers.quats[i]);
          contDummy.updateMatrix();
          containers.mesh.setMatrixAt(i, contDummy.matrix);
        }
        containers.mesh.instanceMatrix.needsUpdate = true;
      }
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
